import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Edit3,
  Minimize2,
  Maximize2,
  Download,
  Upload,
  Save,
  RotateCcw,
  Trash2,
  Expand,
  Shrink,
  ChevronsUpDown,
  Undo2,
  Redo2,
  Mic,
  Monitor,
} from "lucide-react";
import AudioPlayer from "../Audio/AudioPlayer";
import LoopNudgeControls from "../Audio/LoopNudgeControls";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import {
  SECTION_COLORS,
  SECTION_BORDER_COLORS,
} from "../Teleprompter/sectionColors";
import { Keyboard } from "@capacitor/keyboard";

const FloatingNotepad = ({
  notepadState,
  onExportTxt,
  onUploadToSongs,
  onSaveChanges,
  onRevertChanges,
  onStartNewContent,
  hasUnsavedChanges,
  // Audio-related props
  currentSongAudio = null,
  onAudioDownload = null,
  onAudioRemove = null,
  onAudioReplace = null,
  // Loop control props (replaces tab bar when loop is active)
  loopActive = false,
  loopControls = null, // { expandLeft, expandRight, shiftLeft, shiftRight }
  // Loop state callbacks
  onLoopStateChange = null,
  onLoopControlsReady = null,
  // Recording props
  onToggleRecording = null,
  isRecording = false,
  currentSongId = null,
  audioBlob = null,
  recordingDuration = 0,
  onSaveTake = null,
  onResetRecording = null,
  // When true, suppress save-take dialog (loop-record handles its own flow)
  isLoopRecordActive = false,
  // Teleprompter section data for visualization
  teleprompterSections = null,
}) => {
  const {
    content,
    title,
    isMinimized,
    dimensions,
    position,
    updateContent,
    updateTitle,
    toggleMinimized,
    setPosition,
    updateDimensions,
  } = notepadState;

  const containerRef = useRef(null);
  const textareaRef = useRef(null);
  const fullscreenTextareaRef = useRef(null);
  const dragDataRef = useRef(null);
  const resizeDataRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tempPosition, setTempPosition] = useState(position);
  const [tempDimensions, setTempDimensions] = useState(dimensions);
  const [isMobileResizing, setIsMobileResizing] = useState(false);
  const mobileResizeDataRef = useRef(null);

  // Save take dialog state
  const [showSaveTakeDialog, setShowSaveTakeDialog] = useState(false);
  const [saveTakeLabel, setSaveTakeLabel] = useState("");
  const [isSavingTake, setIsSavingTake] = useState(false);
  const prevIsRecordingRef = useRef(false);
  // Track whether the notepad initiated the current recording
  const notepadStartedRecordingRef = useRef(false);

  // Section visualization state
  const [showSections, setShowSections] = useState(false);
  const [notepadWaveSurfer, setNotepadWaveSurfer] = useState(null);
  const sectionRegionsRef = useRef(null);
  const sectionOverlayRef = useRef(null);
  const fullscreenSectionOverlayRef = useRef(null);

  // Detect when recording stops (isRecording: true → false) with a blob ready
  // Skip when isLoopRecordActive — loop-record handles its own splice flow
  // Only show dialog if the notepad started the recording (not the takes panel)
  useEffect(() => {
    if (
      prevIsRecordingRef.current &&
      !isRecording &&
      audioBlob &&
      !isLoopRecordActive &&
      notepadStartedRecordingRef.current
    ) {
      setSaveTakeLabel("");
      setShowSaveTakeDialog(true);
      notepadStartedRecordingRef.current = false;
    }
    prevIsRecordingRef.current = isRecording;
  }, [isRecording, audioBlob, isLoopRecordActive]);

  // Auto-reset showSections when loop deactivates, sections unavailable, or song changes
  useEffect(() => {
    if (!loopActive) setShowSections(false);
  }, [loopActive]);

  useEffect(() => {
    if (!teleprompterSections?.length) setShowSections(false);
  }, [teleprompterSections]);

  useEffect(() => {
    setShowSections(false);
  }, [currentSongId]);

  // Waveform section regions effect
  useEffect(() => {
    // Clean up existing section regions
    if (sectionRegionsRef.current) {
      sectionRegionsRef.current.clearRegions();
      sectionRegionsRef.current.destroy();
      sectionRegionsRef.current = null;
    }

    const ws = notepadWaveSurfer;
    if (!ws) return;

    if (showSections && teleprompterSections?.length) {
      // White waveform with colored section regions
      ws.setOptions({ waveColor: "#ffffff", progressColor: "#e0e0e0" });

      const regions = ws.registerPlugin(RegionsPlugin.create());
      sectionRegionsRef.current = regions;

      teleprompterSections.forEach((section, idx) => {
        const color =
          section.color || SECTION_COLORS[idx % SECTION_COLORS.length];
        regions.addRegion({
          id: `notepad-section-${section.id}`,
          start: section.audioStartSec,
          end: section.audioEndSec,
          color,
          drag: false,
          resize: false,
        });
      });
    } else {
      // Revert to original green/gold colors
      ws.setOptions({ waveColor: "#7ab892", progressColor: "#c5a34e" });
    }

    return () => {
      if (sectionRegionsRef.current) {
        sectionRegionsRef.current.clearRegions();
        sectionRegionsRef.current.destroy();
        sectionRegionsRef.current = null;
      }
    };
  }, [showSections, notepadWaveSurfer, teleprompterSections]);

  const handleSaveTakeConfirm = async () => {
    if (!audioBlob || !currentSongId || !onSaveTake) return;
    setIsSavingTake(true);
    try {
      const label = saveTakeLabel.trim() || undefined;
      await onSaveTake(currentSongId, audioBlob, label, recordingDuration);
    } catch (err) {
      console.error("❌ Failed to save take:", err);
    } finally {
      setIsSavingTake(false);
      setShowSaveTakeDialog(false);
      setSaveTakeLabel("");
      if (onResetRecording) onResetRecording();
    }
  };

  const handleSaveTakeDiscard = () => {
    setShowSaveTakeDialog(false);
    setSaveTakeLabel("");
    if (onResetRecording) onResetRecording();
  };

  // Confirm before erasing notepad if it has content
  const handleStartNewContent = () => {
    if (content.trim()) {
      if (window.confirm("Are you sure you want to erase the notepad?")) {
        onStartNewContent();
      }
    } else {
      onStartNewContent();
    }
  };

  // Store the current transform for immediate DOM updates
  const currentTransformRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // Mobile detection - simplified
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);

  // Tracks how far the minimized bar should sit above the viewport bottom so
  // it floats at the bottom of the screen but rises with the footer when the
  // footer scrolls into view.
  const [minimizedFloor, setMinimizedFloor] = useState(0);

  useEffect(() => {
    if (isMobile || !isMinimized || isFullscreen) return undefined;

    const update = () => {
      const footer = document.querySelector("footer");
      if (!footer) {
        setMinimizedFloor(0);
        return;
      }
      const rect = footer.getBoundingClientRect();
      const overlap = window.innerHeight - rect.top;
      setMinimizedFloor(Math.max(0, overlap));
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    // Recompute when page content height changes (e.g. switching tabs from a
    // long scrollable page back to a short one) — scroll/resize won't fire then.
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(update)
        : null;
    if (resizeObserver) resizeObserver.observe(document.body);

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [isMobile, isMinimized, isFullscreen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Track keyboard state using Capacitor Keyboard plugin
  const [capacitorKeyboardHeight, setCapacitorKeyboardHeight] = useState(0);
  const [isCapacitorKeyboardVisible, setIsCapacitorKeyboardVisible] =
    useState(false);

  // Use Capacitor Keyboard plugin for accurate keyboard detection
  useEffect(() => {
    if (!isMobile) return;

    let showListener;
    let hideListener;

    const setupKeyboardListeners = async () => {
      try {
        showListener = await Keyboard.addListener(
          "keyboardWillShow",
          (info) => {
            setCapacitorKeyboardHeight(info.keyboardHeight);
            setIsCapacitorKeyboardVisible(true);
            setIsKeyboardOpen(true);
            setKeyboardHeight(info.keyboardHeight);
          },
        );

        hideListener = await Keyboard.addListener("keyboardWillHide", () => {
          setCapacitorKeyboardHeight(0);
          setIsCapacitorKeyboardVisible(false);
          setIsKeyboardOpen(false);
          setKeyboardHeight(0);
        });
      } catch (e) {
        // Keyboard plugin not available (running in browser), fall back to visual viewport
        console.log("Capacitor Keyboard plugin not available, using fallback");
      }
    };

    setupKeyboardListeners();

    return () => {
      if (showListener) showListener.remove();
      if (hideListener) hideListener.remove();
    };
  }, [isMobile]);

  // Keyboard is visible when Capacitor reports it
  const keyboardVisible = isMobile && isCapacitorKeyboardVisible;

  // Toggle fullscreen
  const toggleFullscreen = () => {
    console.log("🔍 toggleFullscreen called, current state:", {
      isFullscreen,
      isMinimized,
    });
    if (!isFullscreen && isMinimized) {
      // When entering fullscreen, ensure notepad is not minimized
      console.log("🔍 Expanding notepad before fullscreen");
      toggleMinimized();
    }
    const newFullscreenState = !isFullscreen;
    console.log("🔍 Setting fullscreen to:", newFullscreenState);
    setIsFullscreen(newFullscreenState);
  };

  useEffect(() => {
    setTempPosition(position);
    currentTransformRef.current = {
      ...currentTransformRef.current,
      right: position.right,
      bottom: position.bottom,
    };
  }, [position]);

  useEffect(() => {
    setTempDimensions(dimensions);
    currentTransformRef.current = {
      ...currentTransformRef.current,
      width: dimensions.width,
      height: dimensions.height,
    };
  }, [dimensions]);

  const startDrag = (clientX, clientY) => {
    if (isMinimized) return;
    if (dragDataRef.current) return;
    if (isMobile) return; // Disable dragging on mobile

    const rect = containerRef.current.getBoundingClientRect();
    dragDataRef.current = {
      startX: clientX,
      startY: clientY,
      rect,
    };

    setIsDragging(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "move";

    // Initialize current transform with starting position
    currentTransformRef.current = {
      ...currentTransformRef.current,
      right: tempPosition.right,
      bottom: tempPosition.bottom,
    };

    document.addEventListener("mousemove", handleMouseMove, { passive: false });
    document.addEventListener("mouseup", endDrag);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", endDrag);
  };

  const handleMouseDown = (e) => {
    // Don't start drag if clicking on resize handles, inputs, or buttons
    if (
      e.target.closest("input, button") ||
      e.target.style.cursor.includes("resize") ||
      e.target.title?.includes("Resize")
    )
      return;
    if (isMobile) return; // Disable dragging on mobile
    startDrag(e.clientX, e.clientY);
  };

  const handleTouchStart = (e) => {
    if (e.target.closest("input, button")) return;
    if (isMobile) return; // Disable touch dragging on mobile
    const touch = e.touches[0];
    startDrag(touch.clientX, touch.clientY);
  };

  const handleMouseMove = (e) => {
    if (!dragDataRef.current || !containerRef.current) return;

    const { startX, startY, rect } = dragDataRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const newLeft = rect.left + dx;
    const newTop = rect.top + dy;

    // Keep within viewport bounds
    const maxLeft = window.innerWidth - rect.width;
    const maxTop = window.innerHeight - rect.height;
    const constrainedLeft = Math.max(0, Math.min(maxLeft, newLeft));
    const constrainedTop = Math.max(0, Math.min(maxTop, newTop));

    // Update DOM directly for immediate response using transform for better performance
    const element = containerRef.current;
    const newRight = window.innerWidth - constrainedLeft - rect.width;
    const newBottom = window.innerHeight - constrainedTop - rect.height;

    // Use transform instead of changing position for better performance
    const translateX = constrainedLeft - rect.left;
    const translateY = constrainedTop - rect.top;
    element.style.transform = `translate(${translateX}px, ${translateY}px)`;

    // Store the new position for final state update
    currentTransformRef.current = {
      ...currentTransformRef.current,
      right: newRight,
      bottom: newBottom,
    };
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
  };

  const endDrag = () => {
    if (!dragDataRef.current) return;

    dragDataRef.current = null;
    setIsDragging(false);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", endDrag);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", endDrag);

    // Clear transform and update React state with final position
    if (containerRef.current) {
      containerRef.current.style.transform = "";
    }

    const finalPosition = {
      right: currentTransformRef.current.right,
      bottom: currentTransformRef.current.bottom,
    };
    setTempPosition(finalPosition);
    setPosition(finalPosition);
  };

  // Resize functionality
  const startResize = (direction, clientX, clientY) => {
    if (isMinimized || resizeDataRef.current) return;
    if (isMobile) return; // Disable resizing on mobile

    const rect = containerRef.current.getBoundingClientRect();
    resizeDataRef.current = {
      direction,
      startX: clientX,
      startY: clientY,
      startWidth: rect.width,
      startHeight: rect.height,
      startLeft: rect.left,
      startTop: rect.top,
      startRight: tempPosition.right,
      startBottom: tempPosition.bottom,
    };

    setIsResizing(true);

    // Initialize current transform with starting dimensions and position
    currentTransformRef.current = {
      width: rect.width,
      height: rect.height,
      right: tempPosition.right,
      bottom: tempPosition.bottom,
    };

    document.addEventListener("mousemove", handleResizeMouseMove, {
      passive: false,
    });
    document.addEventListener("mouseup", endResize);
    document.body.style.cursor = getResizeCursor(direction);
    document.body.style.userSelect = "none";
  };

  const handleResizeMouseMove = (e) => {
    if (!resizeDataRef.current || !containerRef.current) return;

    const {
      direction,
      startX,
      startY,
      startWidth,
      startHeight,
      startRight,
      startBottom,
    } = resizeDataRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    let newWidth = startWidth;
    let newHeight = startHeight;
    let newRight = startRight;
    let newBottom = startBottom;

    const minWidth = 200;
    const minHeight = 200;
    const maxWidth = window.innerWidth - 40;
    const maxHeight = window.innerHeight - 40;

    // Handle different resize directions
    if (direction.includes("e")) {
      // East (right edge)
      newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + dx));
    }
    if (direction.includes("w")) {
      // West (left edge)
      const proposedWidth = startWidth - dx;
      newWidth = Math.max(minWidth, Math.min(maxWidth, proposedWidth));
      const actualWidthChange = newWidth - startWidth;
      newRight = startRight - actualWidthChange;
    }
    if (direction.includes("s")) {
      // South (bottom edge)
      newHeight = Math.max(minHeight, Math.min(maxHeight, startHeight + dy));
    }
    if (direction.includes("n")) {
      // North (top edge)
      const proposedHeight = startHeight - dy;
      newHeight = Math.max(minHeight, Math.min(maxHeight, proposedHeight));
      const actualHeightChange = newHeight - startHeight;
      newBottom = startBottom - actualHeightChange;
    }

    // Ensure we don't go off screen
    const maxRight = window.innerWidth - newWidth;
    const maxBottom = window.innerHeight - newHeight;
    newRight = Math.max(0, Math.min(maxRight, newRight));
    newBottom = Math.max(0, Math.min(maxBottom, newBottom));

    // Update DOM directly for immediate response
    const element = containerRef.current;
    element.style.width = `${newWidth}px`;
    element.style.height = `${newHeight}px`;

    // Only update position if it changed to avoid unnecessary reflows
    if (newRight !== startRight || newBottom !== startBottom) {
      element.style.right = `${newRight}px`;
      element.style.bottom = `${newBottom}px`;
    }

    // Store the new dimensions and position for final state update
    currentTransformRef.current = {
      width: newWidth,
      height: newHeight,
      right: newRight,
      bottom: newBottom,
    };
  };

  const endResize = () => {
    if (!resizeDataRef.current) return;

    resizeDataRef.current = null;
    setIsResizing(false);
    document.removeEventListener("mousemove", handleResizeMouseMove);
    document.removeEventListener("mouseup", endResize);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    // Update React state with final dimensions and position
    const finalDimensions = {
      width: currentTransformRef.current.width,
      height: currentTransformRef.current.height,
    };
    const finalPosition = {
      right: currentTransformRef.current.right,
      bottom: currentTransformRef.current.bottom,
    };

    setTempDimensions(finalDimensions);
    setTempPosition(finalPosition);
    updateDimensions(finalDimensions);
    setPosition(finalPosition);
  };

  const getResizeCursor = (direction) => {
    const cursors = {
      n: "ns-resize",
      ne: "ne-resize",
      e: "ew-resize",
      se: "se-resize",
      s: "ns-resize",
      sw: "sw-resize",
      w: "ew-resize",
      nw: "nw-resize",
    };
    return cursors[direction] || "default";
  };

  // Mobile resize via drag handle
  const startMobileResize = (clientY) => {
    if (isMinimized || isFullscreen) return;

    mobileResizeDataRef.current = {
      startY: clientY,
      startHeight: tempDimensions.height,
    };

    setIsMobileResizing(true);
    document.body.style.userSelect = "none";
    document.body.style.touchAction = "none";

    document.addEventListener("touchmove", handleMobileResizeMove, {
      passive: false,
    });
    document.addEventListener("touchend", endMobileResize);
    document.addEventListener("mousemove", handleMobileResizeMouseMove, {
      passive: false,
    });
    document.addEventListener("mouseup", endMobileResize);
  };

  const handleMobileResizeMove = (e) => {
    e.preventDefault();
    if (!mobileResizeDataRef.current) return;

    const touch = e.touches[0];
    const { startY, startHeight } = mobileResizeDataRef.current;

    // Dragging up (negative dy) increases height, dragging down decreases
    const dy = startY - touch.clientY;
    const newHeight = Math.max(
      200,
      Math.min(window.innerHeight - 150, startHeight + dy),
    );

    // Update DOM directly for smooth performance
    if (containerRef.current) {
      containerRef.current.style.height = `${newHeight}px`;
    }

    currentTransformRef.current = {
      ...currentTransformRef.current,
      height: newHeight,
    };
  };

  const handleMobileResizeMouseMove = (e) => {
    if (!mobileResizeDataRef.current) return;

    const { startY, startHeight } = mobileResizeDataRef.current;
    const dy = startY - e.clientY;
    const newHeight = Math.max(
      200,
      Math.min(window.innerHeight - 150, startHeight + dy),
    );

    if (containerRef.current) {
      containerRef.current.style.height = `${newHeight}px`;
    }

    currentTransformRef.current = {
      ...currentTransformRef.current,
      height: newHeight,
    };
  };

  const endMobileResize = () => {
    if (!mobileResizeDataRef.current) return;

    mobileResizeDataRef.current = null;
    setIsMobileResizing(false);
    document.body.style.userSelect = "";
    document.body.style.touchAction = "";

    document.removeEventListener("touchmove", handleMobileResizeMove);
    document.removeEventListener("touchend", endMobileResize);
    document.removeEventListener("mousemove", handleMobileResizeMouseMove);
    document.removeEventListener("mouseup", endMobileResize);

    // Update React state with final height
    const finalHeight = currentTransformRef.current.height;
    if (finalHeight) {
      const finalDimensions = {
        ...tempDimensions,
        height: finalHeight,
      };
      setTempDimensions(finalDimensions);
      updateDimensions(finalDimensions);
    }
  };

  const handleDragHandleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    startMobileResize(touch.clientY);
  };

  const handleDragHandleMouseDown = (e) => {
    e.preventDefault();
    startMobileResize(e.clientY);
  };

  const handleKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    const step = 10;
    let newPos = { ...tempPosition };
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        newPos.bottom = newPos.bottom + step;
        setTempPosition(newPos);
        setPosition(newPos);
        break;
      case "ArrowDown":
        e.preventDefault();
        newPos.bottom = Math.max(0, newPos.bottom - step);
        setTempPosition(newPos);
        setPosition(newPos);
        break;
      case "ArrowLeft":
        e.preventDefault();
        newPos.right = newPos.right + step;
        setTempPosition(newPos);
        setPosition(newPos);
        break;
      case "ArrowRight":
        e.preventDefault();
        newPos.right = Math.max(0, newPos.right - step);
        setTempPosition(newPos);
        setPosition(newPos);
        break;
      case "Escape":
        e.preventDefault();
        newPos = { bottom: 20, right: 20 };
        setTempPosition(newPos);
        setPosition(newPos);
        break;
      default:
    }
  };

  const handleContentChange = (e) => {
    updateContent(e.target.value);
  };

  const handleTitleChange = (e) => {
    updateTitle(e.target.value);
  };

  // Undo/Redo handlers
  const handleUndo = () => {
    const activeTextarea = isFullscreen
      ? fullscreenTextareaRef.current
      : textareaRef.current;
    if (activeTextarea) {
      activeTextarea.focus();
      document.execCommand("undo");
    }
  };

  const handleRedo = () => {
    const activeTextarea = isFullscreen
      ? fullscreenTextareaRef.current
      : textareaRef.current;
    if (activeTextarea) {
      activeTextarea.focus();
      document.execCommand("redo");
    }
  };

  // Track audio player container ref for portal positioning
  // (Must be declared before early returns to follow React hooks rules)
  const audioContainerRef = useRef(null);
  const [audioPosition, setAudioPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Update audio position when container or state changes
  useEffect(() => {
    const updatePosition = () => {
      if (audioContainerRef.current && !isMinimized && !isFullscreen) {
        const rect = audioContainerRef.current.getBoundingClientRect();
        setAudioPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition);

    // Also update when notepad position/dimensions change
    const interval = setInterval(updatePosition, 100);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
      clearInterval(interval);
    };
  }, [isMinimized, isFullscreen, tempPosition, tempDimensions]);

  // Persistent Audio Player Portal - must render even when minimized to preserve audio state
  const persistentAudioPlayer =
    currentSongAudio &&
    createPortal(
      <div
        id="persistent-notepad-audio"
        className="flex-shrink-0 w-full bg-forest-800"
        style={{
          position: "fixed",
          zIndex: 1000000,
          ...(isMinimized
            ? {
                // Hidden when minimized but keep mounted
                opacity: 0,
                pointerEvents: "none",
                top: "-9999px",
              }
            : isFullscreen
              ? {
                  // Fullscreen: position at top after header and loop controls
                  top: `calc(max(32px, calc(8px + env(safe-area-inset-top, 24px))) + 49px${loopActive && loopControls ? " + 36px" : ""})`,
                  left: 0,
                  right: 0,
                  borderBottom: "1px solid #2a5038",
                }
              : audioPosition.width > 0
                ? {
                    // Normal expanded: position over the spacer
                    top: audioPosition.top + "px",
                    left: audioPosition.left + "px",
                    width: audioPosition.width + "px",
                    borderBottom: "1px solid #2a5038",
                  }
                : {
                    display: "none",
                  }),
        }}
      >
        <AudioPlayer
          key={`persistent-audio-${currentSongAudio.url}`}
          audioUrl={currentSongAudio.url}
          audioFilename={currentSongAudio.filename}
          audioSize={currentSongAudio.size}
          audioDuration={currentSongAudio.duration}
          onDownload={onAudioDownload}
          onRemove={onAudioRemove}
          onReplace={onAudioReplace}
          showControls={true}
          compact={true}
          hideMenu={true}
          onLoopStateChange={onLoopStateChange}
          onLoopControlsReady={onLoopControlsReady}
          onWaveSurferReady={setNotepadWaveSurfer}
        />
      </div>,
      document.body,
    );

  // Render FAB for mobile when minimized (but still include audio portal)
  // Portal to document.body to escape any parent stacking context (song cards, etc.)
  if (isMinimized && isMobile && !isFullscreen) {
    return (
      <>
        {persistentAudioPlayer}
        {createPortal(
          <button
            onClick={toggleMinimized}
            className="fixed rounded-full shadow-2xl transition-all duration-300 flex items-center justify-center bg-forest-800 border-forest-100 border-2 hover:bg-forest-700"
            style={{
              bottom: `max(100px, calc(84px + env(safe-area-inset-bottom, 0px)))`,
              right: `calc(16px + env(safe-area-inset-right, 0px))`,
              width: "56px",
              height: "56px",
              zIndex: 999999,
            }}
            title="Open Notepad"
            aria-label="Open Notepad"
          >
            <Edit3 className="w-6 h-6 text-forest-200" />
          </button>,
          document.body,
        )}
      </>
    );
  }

  return (
    <>
      {/* Persistent Audio Player - Rendered via portal to survive fullscreen/minimize transitions */}
      {persistentAudioPlayer}

      {/* Mobile Drag Handle - Shows above notepad when expanded on mobile */}
      {!isFullscreen && !isMinimized && isMobile && (
        <div
          onTouchStart={(e) => {
            e.stopPropagation();
            handleDragHandleTouchStart(e);
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            handleDragHandleMouseDown(e);
          }}
          onClick={(e) => e.stopPropagation()}
          className="fixed flex items-center justify-center cursor-ns-resize"
          style={{
            // Position handle above notepad - adjust for keyboard state
            bottom: keyboardVisible
              ? `${tempDimensions.height}px`
              : `calc(max(80px, calc(64px + env(safe-area-inset-bottom, 0px))) + ${tempDimensions.height}px)`,
            // Center the handle: notepad right edge + half notepad width - half handle width
            right: `${tempPosition.right + tempDimensions.width / 2 - (tempDimensions.width * 0.25) / 2}px`,
            width: `${tempDimensions.width * 0.25}px`,
            minWidth: "80px",
            height: "24px",
            touchAction: "none",
            backgroundColor: "#152a1c",
            border: "2px solid #d0eadb",
            borderRadius: "8px 8px 0 0",
            borderBottom: "none",
            zIndex: 999999,
          }}
        >
          {/* Drag handle icon */}
          <ChevronsUpDown
            className={
              isMobileResizing
                ? "transition-colors text-forest-200"
                : "transition-colors text-forest-300"
            }
            style={{ width: "20px", height: "20px" }}
          />
        </div>
      )}

      {!isFullscreen && (
        <div
          ref={containerRef}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className={`fixed shadow-2xl border glass-card ${
            isDragging || isResizing || isMobileResizing
              ? "transition-none"
              : "transition-all duration-300"
          } ${
            isMinimized
              ? "z-[60] md:z-[70] floating-notepad-minimized"
              : "z-[999999]"
          } bg-forest-800 border-forest-100 border-2 ${!isMinimized ? "floating-notepad-expanded" : ""} ${
            isDragging ? "shadow-3xl scale-[1.02]" : ""
          } ${isResizing || isMobileResizing ? "shadow-3xl" : ""}`}
          style={
            isMinimized
              ? {
                  // Collapsed: Bottom bar - sticks to viewport bottom, but rises
                  // with the footer once the footer scrolls into view (desktop only)
                  bottom: `${minimizedFloor}px`,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "min(300px, calc(100vw - 32px))",
                  height: "40px",
                  borderRadius: "8px",
                  resize: "none",
                  overflow: "hidden",
                  border: "2px solid #d0eadb !important",
                  display: isMobile ? "none" : "block", // Hide bar on mobile, show FAB instead
                }
              : {
                  // Expanded: Floating window
                  // When keyboard is open on mobile, use TOP positioning based on visual viewport
                  // This ensures the notepad sits just above the keyboard
                  // On mobile: position based on keyboard state using Capacitor keyboard height
                  // When keyboard visible, position at bottom of screen (adjustResize shrinks viewport)
                  // When keyboard hidden, position directly above bottom nav bar (matches navbar height)
                  bottom: isMobile
                    ? keyboardVisible
                      ? "0px"
                      : `max(80px, calc(64px + env(safe-area-inset-bottom, 0px)))`
                    : `${tempPosition.bottom}px`,
                  right: isMobile
                    ? `calc(${Math.max(tempPosition.right, 8)}px + env(safe-area-inset-right, 0px))`
                    : `${tempPosition.right}px`,
                  width: isMobile
                    ? `calc(${Math.max(tempDimensions.width, 280)}px - env(safe-area-inset-right, 0px))`
                    : `${tempDimensions.width}px`,
                  height: `${tempDimensions.height}px`,
                  borderRadius: "8px",
                  resize: "none", // Disable default resize, we'll use custom handles
                  overflow: "hidden",
                  minWidth: isMobile ? "280px" : "200px",
                  minHeight: "200px",
                  border: "2px solid #d0eadb !important",
                  display: "flex",
                  flexDirection: "column",
                }
          }
        >
          {/* Header - Contains title and buttons */}
          <div
            className={`flex-shrink-0 flex items-center p-2 ${
              isMinimized ? "" : "border-b"
            } border-forest-600 bg-forest-700 ${isMobile ? "cursor-default" : "cursor-move"}`}
            style={{ flexDirection: "row", justifyContent: "space-between" }}
            onMouseDown={isMobile ? undefined : handleMouseDown}
            onTouchStart={isMobile ? undefined : handleTouchStart}
          >
            {/* Left side - Icon + Title or Notepad label */}
            <div
              className="flex items-center gap-1 md:gap-2"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                ...(!isMobile &&
                  !isMinimized && {
                    maxWidth: "min(50%, 360px)",
                  }),
                ...(!isMobile && {
                  transform: "scale(1.0)",
                  transformOrigin: "left center",
                }),
              }}
            >
              <Edit3 className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0 text-forest-300" />
              {isMinimized ? (
                <span
                  className="text-sm font-medium cursor-pointer truncate text-gold-500"
                  onClick={toggleMinimized}
                >
                  {title || "Notepad"}
                  {hasUnsavedChanges ? "*" : ""}
                </span>
              ) : (
                <input
                  type="text"
                  value={title + (hasUnsavedChanges ? "*" : "")}
                  onChange={(e) =>
                    handleTitleChange({
                      target: { value: e.target.value.replace("*", "") },
                    })
                  }
                  onFocus={(e) => e.target.setSelectionRange(0, 0)}
                  placeholder="Title..."
                  className="text-xs md:text-sm border rounded bg-forest-700 border-forest-600 text-gold-500 placeholder-forest-300"
                  style={{
                    flex: "1 1 0",
                    minWidth: 0,
                    width: 0,
                    padding: isMobile ? "2px 4px" : "4px 8px",
                    boxSizing: "border-box",
                    textOverflow: "ellipsis",
                  }}
                />
              )}
            </div>

            {/* Right side - Export buttons + Minimize/Maximize button */}
            <div
              className={`flex items-center flex-shrink-0 notepad-header-buttons ${isMobile ? "gap-0.5 pl-1" : "gap-1 pr-1"}`}
              style={
                !isMobile
                  ? {
                      transform: "scale(0.84)",
                      transformOrigin: "right center",
                    }
                  : undefined
              }
            >
              {/* Buttons for MINIMIZED state - only show on mobile (desktop shows only expand button) */}
              {isMinimized && isMobile && (
                <>
                  {/* Undo Button */}
                  <button
                    onClick={handleUndo}
                    className="p-0.5 rounded text-xs transition-colors bg-forest-600 hover:bg-forest-500 text-forest-200"
                    title="Undo"
                  >
                    <Undo2 style={{ width: "14px", height: "14px" }} />
                  </button>

                  {/* Redo Button */}
                  <button
                    onClick={handleRedo}
                    className="p-0.5 rounded text-xs transition-colors bg-forest-600 hover:bg-forest-500 text-forest-200"
                    title="Redo"
                  >
                    <Redo2 style={{ width: "14px", height: "14px" }} />
                  </button>

                  {/* Upload/Save Button */}
                  <button
                    onClick={
                      notepadState.currentEditingSongId
                        ? onSaveChanges
                        : onUploadToSongs
                    }
                    disabled={!content.trim()}
                    className={`p-0.5 rounded text-xs transition-colors ${
                      content.trim()
                        ? notepadState.currentEditingSongId
                          ? "bg-gold-500 hover:bg-gold-400 text-forest-900"
                          : "bg-leaf-500 hover:bg-leaf-400 text-forest-900"
                        : "bg-forest-600 text-forest-500 cursor-not-allowed"
                    }`}
                    title={
                      notepadState.currentEditingSongId
                        ? "Save Changes"
                        : "Add to Songs"
                    }
                  >
                    {notepadState.currentEditingSongId ? (
                      <Save
                        style={{
                          width: "14px",
                          height: "14px",
                          color: "#000000",
                        }}
                      />
                    ) : (
                      <Upload
                        style={{
                          width: "14px",
                          height: "14px",
                          color: "#c5a34e",
                        }}
                      />
                    )}
                  </button>

                  {/* New Tab Button */}
                  <button
                    onClick={handleStartNewContent}
                    className="p-0.5 rounded text-xs transition-colors bg-ember-500 hover:bg-ember-400 text-forest-900"
                    title="New Empty Tab"
                  >
                    <Trash2
                      style={{
                        width: "14px",
                        height: "14px",
                        color: "#000000",
                      }}
                    />
                  </button>
                </>
              )}

              {/* Buttons for EXPANDED state */}
              {!isMinimized && (
                <>
                  {/* Undo Button */}
                  <button
                    onClick={handleUndo}
                    className={`${isMobile ? "p-0.5" : "p-1"} rounded text-xs transition-colors bg-forest-600 hover:bg-forest-500 text-forest-200`}
                    title="Undo"
                  >
                    <Undo2
                      style={{
                        width: isMobile ? "14px" : "16px",
                        height: isMobile ? "14px" : "16px",
                      }}
                    />
                  </button>

                  {/* Redo Button */}
                  <button
                    onClick={handleRedo}
                    className={`${isMobile ? "p-0.5" : "p-1"} rounded text-xs transition-colors bg-forest-600 hover:bg-forest-500 text-forest-200`}
                    title="Redo"
                  >
                    <Redo2
                      style={{
                        width: isMobile ? "14px" : "16px",
                        height: isMobile ? "14px" : "16px",
                      }}
                    />
                  </button>

                  {/* Upload/Save Button */}
                  <button
                    onClick={
                      notepadState.currentEditingSongId
                        ? onSaveChanges
                        : onUploadToSongs
                    }
                    disabled={!content.trim()}
                    className={`${isMobile ? "p-0.5" : "p-1"} rounded text-xs transition-colors ${
                      content.trim()
                        ? notepadState.currentEditingSongId
                          ? "bg-gold-500 hover:bg-gold-400 text-forest-900"
                          : "bg-leaf-500 hover:bg-leaf-400 text-forest-900"
                        : "bg-forest-600 text-forest-500 cursor-not-allowed"
                    }`}
                    title={
                      notepadState.currentEditingSongId
                        ? "Save Changes"
                        : "Add to Songs"
                    }
                  >
                    {notepadState.currentEditingSongId ? (
                      <Save
                        style={{
                          width: isMobile ? "14px" : "16px",
                          height: isMobile ? "14px" : "16px",
                          color: "#000000",
                        }}
                      />
                    ) : (
                      <Upload
                        style={{
                          width: isMobile ? "14px" : "16px",
                          height: isMobile ? "14px" : "16px",
                          color: "#c5a34e",
                        }}
                      />
                    )}
                  </button>

                  {/* New Tab Button */}
                  <button
                    onClick={handleStartNewContent}
                    className={`${isMobile ? "p-0.5" : "p-1"} rounded text-xs transition-colors bg-ember-500 hover:bg-ember-400 text-forest-900`}
                    title="New Empty Tab"
                  >
                    <Trash2
                      style={{
                        width: isMobile ? "14px" : "16px",
                        height: isMobile ? "14px" : "16px",
                        color: "#000000",
                      }}
                    />
                  </button>
                </>
              )}

              <button
                onClick={toggleMinimized}
                className={`${isMobile ? "p-0.5" : "p-1"} rounded hover:bg-opacity-20 hover:bg-forest-500 text-forest-300`}
              >
                {isMinimized ? (
                  <Maximize2
                    style={{
                      width: isMobile ? "14px" : "16px",
                      height: isMobile ? "14px" : "16px",
                    }}
                  />
                ) : (
                  <Minimize2
                    style={{
                      width: isMobile ? "14px" : "16px",
                      height: isMobile ? "14px" : "16px",
                    }}
                  />
                )}
              </button>
            </div>
          </div>

          {/* Loop Nudge Controls - Show when not minimized and loop is active */}
          {!isMinimized && loopActive && loopControls && (
            <div className="flex-shrink-0 flex items-center">
              <div style={{ flex: 1 }}>
                <LoopNudgeControls
                  onExpandLeft={loopControls.expandLeft}
                  onExpandRight={loopControls.expandRight}
                  onContractLeft={loopControls.contractLeft}
                  onContractRight={loopControls.contractRight}
                  disabled={false}
                  isMobile={isMobile}
                />
              </div>
              {teleprompterSections?.length > 0 && (
                <button
                  onClick={() => setShowSections((s) => !s)}
                  className={`p-1 mr-1 rounded transition-colors ${
                    showSections
                      ? "text-gold-500"
                      : "text-forest-400 hover:text-forest-200"
                  }`}
                  title={
                    showSections
                      ? "Hide teleprompter sections"
                      : "Show teleprompter sections"
                  }
                >
                  <Monitor style={{ width: "14px", height: "14px" }} />
                </button>
              )}
            </div>
          )}

          {/* Audio Player Spacer - reserves space when audio exists (actual player is in portal) */}
          {!isMinimized && currentSongAudio && (
            <div
              className="flex-shrink-0 border-b w-full border-forest-600"
              style={{ minHeight: "52px" }}
              ref={audioContainerRef}
            />
          )}

          {/* Content - Full-size textarea when not minimized */}
          {!isMinimized && (
            <div style={{ flex: 1, position: "relative", minHeight: 0, display: "flex" }}>
              {showSections && teleprompterSections?.length > 0 && (
                <div
                  ref={sectionOverlayRef}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "4px",
                    overflow: "hidden",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                >
                  {content.split("\n").map((_, lineIdx) => {
                    const section = teleprompterSections.find(
                      (s) =>
                        lineIdx + 1 >= s.startLine && lineIdx + 1 <= s.endLine,
                    );
                    const sectionIdx = section
                      ? teleprompterSections.indexOf(section)
                      : -1;
                    const ta = textareaRef.current;
                    const lineHeight = ta
                      ? parseFloat(getComputedStyle(ta).lineHeight) ||
                        parseFloat(getComputedStyle(ta).fontSize) * 1.5
                      : 21;
                    return (
                      <div
                        key={lineIdx}
                        style={{
                          height: `${lineHeight}px`,
                          borderLeft:
                            sectionIdx >= 0
                              ? `3px solid ${SECTION_BORDER_COLORS[sectionIdx % SECTION_BORDER_COLORS.length]}`
                              : "3px solid transparent",
                        }}
                      />
                    );
                  })}
                </div>
              )}
              <textarea
                ref={textareaRef}
                value={content}
                onChange={handleContentChange}
                onFocus={() => setIsTextareaFocused(true)}
                onBlur={() => setIsTextareaFocused(false)}
                onScroll={(e) => {
                  if (sectionOverlayRef.current) {
                    sectionOverlayRef.current.scrollTop = e.target.scrollTop;
                  }
                }}
                placeholder="Start writing your lyrics..."
                className="flex-1 resize-none border-none outline-none text-sm p-3 bg-forest-800 text-forest-200 placeholder-forest-400"
                style={{
                  width: "100%",
                  minHeight: "0",
                  resize: "none",
                  border: "none",
                  outline: "none",
                  boxSizing: "border-box",
                  paddingLeft:
                    showSections && teleprompterSections?.length > 0
                      ? "16px"
                      : "12px",
                }}
              />
            </div>
          )}

          {/* Bottom bar with word count and fullscreen button - separate from textarea */}
          {!isMinimized && (
            <div
              className="flex-shrink-0 flex items-center justify-between px-2 py-1 border-t bg-forest-800 border-forest-600"
              style={{ minHeight: "36px" }}
            >
              {/* Character count */}
              <div className="text-xs text-forest-300">
                {content.trim() ? content.trim().split(/\s+/).length : 0} words
                • {content.length} chars
              </div>

              {/* Record + Fullscreen buttons grouped on right */}
              <div className="flex items-center gap-1.5">
                {onToggleRecording && (
                  <button
                    onClick={() => {
                      if (!isRecording)
                        notepadStartedRecordingRef.current = true;
                      onToggleRecording();
                    }}
                    className={`w-8 h-8 rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center border hover:scale-110 ${
                      isRecording
                        ? "bg-ember-500 border-ember-400 text-forest-900"
                        : "bg-forest-700 hover:bg-forest-600 border-forest-600 text-gold-500"
                    }`}
                    title={isRecording ? "Stop recording" : "Record take"}
                  >
                    <Mic className="w-4 h-4" />
                  </button>
                )}

                <button
                  onClick={toggleFullscreen}
                  className="w-8 h-8 rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center mobile-fullscreen-btn bg-forest-700 hover:bg-forest-600 text-forest-100 border border-forest-600 hover:scale-110"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <Shrink className="w-4 h-4" />
                  ) : (
                    <Expand className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Resize handles - Only show when expanded and on desktop */}
          {!isMinimized && !isMobile && (
            <>
              {/* Corner handles - Invisible but functional */}
              <div
                className="absolute w-6 h-6 cursor-nw-resize resize-handle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startResize("nw", e.clientX, e.clientY);
                }}
                style={{
                  top: "-3px",
                  left: "-3px",
                  zIndex: 1000,
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: "2px",
                  cursor: "nw-resize", // Explicitly set cursor in style
                }}
                title="Resize from top-left corner"
              />
              <div
                className="absolute w-6 h-6 cursor-ne-resize resize-handle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startResize("ne", e.clientX, e.clientY);
                }}
                style={{
                  top: "-3px",
                  right: "-3px",
                  zIndex: 1000,
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: "2px",
                  cursor: "ne-resize", // Explicitly set cursor in style
                }}
                title="Resize from top-right corner"
              />
              <div
                className="absolute w-6 h-6 cursor-sw-resize resize-handle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startResize("sw", e.clientX, e.clientY);
                }}
                style={{
                  bottom: "-3px",
                  left: "-3px",
                  zIndex: 1000,
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: "2px",
                  cursor: "sw-resize", // Explicitly set cursor in style
                }}
                title="Resize from bottom-left corner"
              />
              <div
                className="absolute w-6 h-6 cursor-se-resize resize-handle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startResize("se", e.clientX, e.clientY);
                }}
                style={{
                  bottom: "-3px",
                  right: "-3px",
                  zIndex: 1000,
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: "2px",
                  cursor: "se-resize", // Explicitly set cursor in style
                }}
                title="Resize from bottom-right corner"
              />

              {/* Edge handles - Also made invisible */}
              <div
                className="absolute h-3 cursor-n-resize resize-handle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startResize("n", e.clientX, e.clientY);
                }}
                style={{
                  top: "-2px",
                  left: "6px",
                  right: "6px",
                  zIndex: 1000,
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: "n-resize", // Explicitly set cursor in style
                }}
                title="Resize from top edge"
              />
              <div
                className="absolute h-3 cursor-s-resize resize-handle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startResize("s", e.clientX, e.clientY);
                }}
                style={{
                  bottom: "-2px",
                  left: "6px",
                  right: "6px",
                  zIndex: 1000,
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: "s-resize", // Explicitly set cursor in style
                }}
                title="Resize from bottom edge"
              />
              <div
                className="absolute w-3 cursor-w-resize resize-handle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startResize("w", e.clientX, e.clientY);
                }}
                style={{
                  top: "6px",
                  bottom: "6px",
                  left: "-2px",
                  zIndex: 1000,
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: "w-resize", // Explicitly set cursor in style
                }}
                title="Resize from left edge"
              />
              <div
                className="absolute w-3 cursor-e-resize resize-handle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  startResize("e", e.clientX, e.clientY);
                }}
                style={{
                  top: "6px",
                  bottom: "6px",
                  right: "-2px",
                  zIndex: 1000,
                  backgroundColor: "transparent",
                  border: "none",
                  cursor: "e-resize", // Explicitly set cursor in style
                }}
                title="Resize from right edge"
              />
            </>
          )}
        </div>
      )}

      {isFullscreen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 999999,
            backgroundColor: "#152a1c",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header - Matches minimized/expanded notepad header exactly */}
          <div
            className="flex items-center p-2 border-b border-forest-600 bg-forest-700"
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              paddingTop:
                "max(32px, calc(8px + env(safe-area-inset-top, 24px)))",
              paddingLeft: "max(8px, env(safe-area-inset-left, 8px))",
              paddingRight: "max(8px, env(safe-area-inset-right, 8px))",
            }}
          >
            {/* Left side - Icon + Title */}
            <div
              className="flex items-center gap-1 md:gap-2 min-w-0"
              style={{
                flex: "1 1 0",
                minWidth: 0,
              }}
            >
              <Edit3 className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0 text-forest-300" />
              <input
                type="text"
                value={title + (hasUnsavedChanges ? "*" : "")}
                onChange={(e) => updateTitle(e.target.value.replace("*", ""))}
                placeholder="Title..."
                className="px-1 md:px-2 py-0.5 md:py-1 text-xs md:text-sm border rounded min-w-0 bg-forest-700 border-forest-600 text-gold-500 placeholder-forest-300"
                style={{ flex: "1 1 0", minWidth: 0 }}
              />
            </div>

            {/* Right side - Buttons matching minimized/expanded notepad */}
            <div className="flex items-center flex-shrink-0 notepad-header-buttons gap-0.5">
              {/* Undo Button */}
              <button
                onClick={handleUndo}
                className="p-0.5 rounded text-xs transition-colors bg-forest-600 hover:bg-forest-500 text-forest-200"
                title="Undo"
              >
                <Undo2 style={{ width: "14px", height: "14px" }} />
              </button>

              {/* Redo Button */}
              <button
                onClick={handleRedo}
                className="p-0.5 rounded text-xs transition-colors bg-forest-600 hover:bg-forest-500 text-forest-200"
                title="Redo"
              >
                <Redo2 style={{ width: "14px", height: "14px" }} />
              </button>

              {/* Upload/Save Button */}
              <button
                onClick={
                  notepadState.currentEditingSongId
                    ? onSaveChanges
                    : onUploadToSongs
                }
                disabled={!content.trim()}
                className={`p-0.5 rounded text-xs transition-colors ${
                  content.trim()
                    ? notepadState.currentEditingSongId
                      ? "bg-gold-500 hover:bg-gold-400 text-forest-900"
                      : "bg-leaf-500 hover:bg-leaf-400 text-forest-900"
                    : "bg-forest-600 text-forest-500 cursor-not-allowed"
                }`}
                title={
                  notepadState.currentEditingSongId
                    ? "Save Changes"
                    : "Add to Songs"
                }
              >
                {notepadState.currentEditingSongId ? (
                  <Save
                    style={{
                      width: "14px",
                      height: "14px",
                      color: "#000000",
                    }}
                  />
                ) : (
                  <Upload
                    style={{
                      width: "14px",
                      height: "14px",
                      color: "#000000",
                    }}
                  />
                )}
              </button>

              {/* New Tab Button */}
              <button
                onClick={handleStartNewContent}
                className="p-0.5 rounded text-xs transition-colors bg-ember-500 hover:bg-ember-400 text-forest-900"
                title="New Empty Tab"
              >
                <Trash2
                  style={{
                    width: "14px",
                    height: "14px",
                    color: "#000000",
                  }}
                />
              </button>

              {/* Exit fullscreen button */}
              <button
                onClick={toggleFullscreen}
                className="p-0.5 rounded hover:bg-opacity-20 hover:bg-forest-500 text-forest-300"
                title="Exit Fullscreen"
              >
                <Shrink style={{ width: "14px", height: "14px" }} />
              </button>
            </div>
          </div>

          {/* Loop Nudge Controls - Show when loop is active */}
          {loopActive && loopControls && (
            <div className="flex items-center">
              <div style={{ flex: 1 }}>
                <LoopNudgeControls
                  onExpandLeft={loopControls.expandLeft}
                  onExpandRight={loopControls.expandRight}
                  onContractLeft={loopControls.contractLeft}
                  onContractRight={loopControls.contractRight}
                  disabled={false}
                  isMobile={isMobile}
                />
              </div>
              {teleprompterSections?.length > 0 && (
                <button
                  onClick={() => setShowSections((s) => !s)}
                  className={`p-1 mr-1 rounded transition-colors ${
                    showSections
                      ? "text-gold-500"
                      : "text-forest-400 hover:text-forest-200"
                  }`}
                  title={
                    showSections
                      ? "Hide teleprompter sections"
                      : "Show teleprompter sections"
                  }
                >
                  <Monitor style={{ width: "14px", height: "14px" }} />
                </button>
              )}
            </div>
          )}

          {/* Audio Player Spacer - reserves space (actual player is in portal) */}
          {currentSongAudio && (
            <div
              className="flex-shrink-0 border-b w-full border-forest-600"
              style={{ minHeight: "52px" }}
            />
          )}

          {/* Fullscreen Textarea */}
          <div
            style={{
              flex: 1,
              position: "relative",
              backgroundColor: "#152a1c",
              minHeight: "300px",
            }}
          >
            {showSections && teleprompterSections?.length > 0 && (
              <div
                ref={fullscreenSectionOverlayRef}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "4px",
                  overflow: "hidden",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                {content.split("\n").map((_, lineIdx) => {
                  const section = teleprompterSections.find(
                    (s) =>
                      lineIdx + 1 >= s.startLine && lineIdx + 1 <= s.endLine,
                  );
                  const sectionIdx = section
                    ? teleprompterSections.indexOf(section)
                    : -1;
                  const lineHeight = 27; // 18px fontSize * 1.5
                  return (
                    <div
                      key={lineIdx}
                      style={{
                        height: `${lineHeight}px`,
                        borderLeft:
                          sectionIdx >= 0
                            ? `3px solid ${SECTION_BORDER_COLORS[sectionIdx % SECTION_BORDER_COLORS.length]}`
                            : "3px solid transparent",
                      }}
                    />
                  );
                })}
              </div>
            )}
            <textarea
              ref={fullscreenTextareaRef}
              value={content}
              onChange={handleContentChange}
              onScroll={(e) => {
                if (fullscreenSectionOverlayRef.current) {
                  fullscreenSectionOverlayRef.current.scrollTop =
                    e.target.scrollTop;
                }
              }}
              placeholder="Start writing your lyrics..."
              style={{
                width: "100%",
                height: "100%",
                minHeight: "300px",
                border: "none",
                outline: "none",
                padding: "16px",
                paddingLeft:
                  showSections && teleprompterSections?.length > 0
                    ? "20px"
                    : "16px",
                fontSize: "18px",
                backgroundColor: "#1e3a28",
                color: "#a5d4b8",
                resize: "none",
              }}
            />

            {/* Character count - positioned in bottom left on mobile, bottom right on desktop */}
            <div
              style={{
                position: "absolute",
                bottom: "8px",
                left: isMobile ? "8px" : "auto",
                right: isMobile ? "auto" : "8px",
                fontSize: "12px",
                color: "#a5d4b8",
                backgroundColor: "rgba(21, 42, 28, 0.8)",
                padding: "4px 8px",
                borderRadius: "4px",
                pointerEvents: "none",
              }}
            >
              {content.trim() ? content.trim().split(/\s+/).length : 0} words •{" "}
              {content.length} chars
            </div>
          </div>
        </div>
      )}
      {/* Save Take Dialog */}
      {showSaveTakeDialog &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={handleSaveTakeDiscard}
          >
            <div
              className="bg-forest-800 border border-forest-700 rounded-lg shadow-xl"
              style={{ width: "280px", padding: "16px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-sm font-medium text-forest-100 mb-3">
                Save Take
              </div>
              <input
                type="text"
                value={saveTakeLabel}
                onChange={(e) => setSaveTakeLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveTakeConfirm();
                  if (e.key === "Escape") handleSaveTakeDiscard();
                }}
                placeholder="Take label (optional)"
                maxLength={100}
                className="w-full px-3 py-2 text-sm bg-forest-900 border border-forest-600 rounded text-forest-200 focus:outline-none focus:border-gold-500 mb-3"
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={handleSaveTakeDiscard}
                  disabled={isSavingTake}
                  className="px-3 py-1.5 text-xs rounded transition-colors bg-forest-700 hover:bg-forest-600 text-forest-200"
                >
                  Discard
                </button>
                <button
                  onClick={handleSaveTakeConfirm}
                  disabled={isSavingTake}
                  className="px-3 py-1.5 text-xs rounded transition-colors bg-gold-500 hover:bg-gold-400 text-forest-900 font-medium"
                >
                  {isSavingTake ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default FloatingNotepad;
